import { useCallback } from 'react';

import type { Account } from './Planner.types';
import { useTrelloTogglePlannerCalendarMutation } from './TrelloTogglePlannerCalendarMutation.generated';
import { usePlannerAccount } from './usePlannerAccount';
import { usePlannerAccounts } from './usePlannerAccounts';
import { useWorkspaceAri } from './useWorkspaceAri';

/**
 * Toggles a calendar's visibility on or off.
 *
 * @returns - A function that handles the calendar toggling.
 * @returns {string | undefined} - The calendar id of the calendar being toggled.
 */
export const useToggleCalendar = () => {
  const { workspaceAri } = useWorkspaceAri();
  // Not all instances that call useToggleCalendar will be wrapped in a account context provider
  // In these scenarios we will have to rely on manually passing the account type and plannerAccountId
  const { plannerAccountId, accountType } = usePlannerAccount();
  const { getEnabledCalendarsForAccount } = usePlannerAccounts();

  const [togglePlannerCalendar, { loading }] =
    useTrelloTogglePlannerCalendarMutation();

  const toggleCalendar = useCallback(
    async ({
      providerCalendarId,
      account,
      start,
      end,
      newCheckedState,
      traceId,
    }: {
      providerCalendarId: string;
      account?: Account;
      start: string;
      end: string;
      newCheckedState: boolean;
      traceId?: string;
    }): Promise<string | undefined> => {
      if (loading || !plannerAccountId || !workspaceAri) {
        return;
      }

      const enabledCalendars = getEnabledCalendarsForAccount(
        account?.id ?? plannerAccountId,
      );

      const calendarId = enabledCalendars?.find(
        (edge) => edge?.providerCalendarId === providerCalendarId,
      )?.id;

      const toggledCalendar = await togglePlannerCalendar({
        variables: {
          enabled: newCheckedState,
          providerAccountId: account?.id ?? plannerAccountId,
          providerCalendarId: account?.displayName ?? providerCalendarId,
          workspaceId: workspaceAri,
          type: account?.accountType ?? accountType ?? 'GOOGLE',
          start,
          end,
        },
        context: {
          traceId,
        },
        // Apollo doesn't allow you to optimistically use cache.evict so we have to use modify to evict
        // https://github.com/apollographql/apollo-client/issues/10289
        ...(!newCheckedState &&
          calendarId && {
            optimisticResponse: {
              __typename: 'Mutation',
              trello: {
                __typename: 'TrelloMutationApi',
                createOrUpdatePlannerCalendar: {
                  errors: null,
                  plannerCalendarMutated: {
                    __typename: 'TrelloPlannerCalendarDeleted',
                    id: calendarId,
                  },
                  success: true,
                  __typename: 'TrelloCreateOrUpdatePlannerCalendarPayload',
                },
              },
            },
            update: (cache, { data }) => {
              if (data?.trello?.createOrUpdatePlannerCalendar?.success) {
                cache.modify({
                  id: cache.identify({
                    __typename: 'TrelloPlannerCalendar',
                    id: calendarId,
                  }),
                  fields: (_, { DELETE }) => DELETE,
                });
              }
            },
          }),
      });

      // Dont set the toggled calendar id if we are toggling off a calendar
      if (
        !(
          toggledCalendar.data?.trello.createOrUpdatePlannerCalendar
            ?.plannerCalendarMutated?.__typename ===
          'TrelloPlannerCalendarDeleted'
        )
      ) {
        return toggledCalendar.data?.trello.createOrUpdatePlannerCalendar
          ?.plannerCalendarMutated?.enabledCalendars?.edges?.[0].node?.id;
      }
    },
    [
      loading,
      plannerAccountId,
      workspaceAri,
      togglePlannerCalendar,
      accountType,
      getEnabledCalendarsForAccount,
    ],
  );

  return { toggleCalendar };
};
